/**
 * Upload page — two paths: paste a single email, or drop a CSV.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LeadsAPI, errorMessage } from '../services/api';

export default function Upload() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('manual');

    // ---- Manual entry state ----
    const [form, setForm] = useState({ email_text: '', company_name: '', contact_name: '' });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // ---- CSV state ----
    const [file, setFile] = useState(null);
    const [csvBusy, setCsvBusy] = useState(false);
    const [csvError, setCsvError] = useState(null);
    const [csvResult, setCsvResult] = useState(null);

    function update(field) {
        return (e) => setForm({ ...form, [field]: e.target.value });
    }

    async function handleManualSubmit(e) {
        e.preventDefault();
        if (form.email_text.trim().length < 5) {
            setError('Paste the lead email or message — at least a sentence.');
            return;
        }
        try {
            setBusy(true); setError(null); setSuccess(null);
            await LeadsAPI.create(form);
            setSuccess('Lead saved and queued for analysis. Redirecting to dashboard…');
            setForm({ email_text: '', company_name: '', contact_name: '' });
            setTimeout(() => navigate('/'), 800);
        } catch (err) {
            setError(errorMessage(err));
        } finally { setBusy(false); }
    }

    async function handleCsvSubmit(e) {
        e.preventDefault();
        if (!file) { setCsvError('Pick a .csv file first.'); return; }
        try {
            setCsvBusy(true); setCsvError(null); setCsvResult(null);
            const data = await LeadsAPI.uploadCsv(file);
            setCsvResult(data);
            setTimeout(() => navigate('/'), 1200);
        } catch (err) {
            setCsvError(errorMessage(err));
        } finally { setCsvBusy(false); }
    }

    return (
        <div className="max-w-3xl mx-auto space-y-4">
            <h1 className="text-2xl font-semibold text-gray-900">Add new leads</h1>

            <div className="bg-white border border-gray-200 rounded-lg">
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setTab('manual')}
                        className={`px-4 py-2 text-sm font-medium ${tab === 'manual' ? 'text-brand-700 border-b-2 border-brand-500' : 'text-gray-500'}`}
                    >Paste a lead</button>
                    <button
                        onClick={() => setTab('csv')}
                        className={`px-4 py-2 text-sm font-medium ${tab === 'csv' ? 'text-brand-700 border-b-2 border-brand-500' : 'text-gray-500'}`}
                    >Upload CSV</button>
                </div>

                {tab === 'manual' && (
                    <form onSubmit={handleManualSubmit} className="p-5 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Contact name</label>
                                <input
                                    value={form.contact_name}
                                    onChange={update('contact_name')}
                                    className="mt-1 w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Company</label>
                                <input
                                    value={form.company_name}
                                    onChange={update('company_name')}
                                    className="mt-1 w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email or message body</label>
                            <textarea
                                rows={8}
                                value={form.email_text}
                                onChange={update('email_text')}
                                placeholder="Paste the prospect's email or your conversation here…"
                                className="mt-1 w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
                            />
                        </div>

                        {error && <div className="text-sm text-red-600">{error}</div>}
                        {success && <div className="text-sm text-green-700">{success}</div>}

                        <button
                            type="submit"
                            disabled={busy}
                            className="px-4 py-2 rounded bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-50"
                        >
                            {busy ? 'Saving…' : 'Save & analyze'}
                        </button>
                    </form>
                )}

                {tab === 'csv' && (
                    <form onSubmit={handleCsvSubmit} className="p-5 space-y-4">
                        <p className="text-sm text-gray-600">
                            CSV with a header row. Recognized columns (case-insensitive):{' '}
                            <code className="px-1 rounded bg-gray-100 text-xs">email_text</code>,{' '}
                            <code className="px-1 rounded bg-gray-100 text-xs">company_name</code>,{' '}
                            <code className="px-1 rounded bg-gray-100 text-xs">contact_name</code>.
                        </p>

                        <input
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="block w-full text-sm text-gray-600
                                       file:mr-3 file:py-2 file:px-3 file:rounded file:border-0
                                       file:text-sm file:font-medium
                                       file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                        />

                        {csvError && <div className="text-sm text-red-600">{csvError}</div>}
                        {csvResult && (
                            <div className="text-sm text-green-700">
                                Imported {csvResult.created} lead{csvResult.created === 1 ? '' : 's'}. Redirecting…
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={csvBusy || !file}
                            className="px-4 py-2 rounded bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-50"
                        >
                            {csvBusy ? 'Uploading…' : 'Upload CSV'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
